import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { INR, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";

const lineTotal = (it) => {
  const base = Number(it.negotiated_price || it.original_price || 0) * Number(it.quantity || 1);
  return base + base * Number(it.tax_percent || 0) / 100;
};

export default function QuotationPrint() {
  const { id } = useParams();
  const [q, setQ] = useState(null);
  const [client, setClient] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    (async () => {
      const r = await api.get(`/quotations/${id}`);
      setQ(r.data);
      const c = await api.get(`/clients/${r.data.client_id}`);
      setClient(c.data);
      try { const s = await api.get("/settings"); setSettings(s.data); } catch (_) {}
    })();
  }, [id]);

  useEffect(() => {
    if (q && client) {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [q, client]);

  if (!q || !client) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { margin: 15mm; size: A4; }
          body { background: white !important; }
        }
        .print-page { background: white; color: #111; }
        .print-page * { color: #111; border-color: #ddd !important; }
      `}</style>
      <div className="min-h-screen bg-muted/40 py-8 print-page">
        <div className="max-w-3xl mx-auto bg-white border border-border rounded-md p-10 shadow-sm" data-testid="quote-print-page">
          <div className="no-print mb-4 flex justify-end gap-2">
            <Button onClick={() => window.print()} className="bg-primary hover:bg-primary/90" data-testid="print-btn">
              <Printer size={14} className="mr-1" />Print / Save PDF
            </Button>
            <Button variant="outline" onClick={() => window.close()}>Close</Button>
          </div>

          {/* Header */}
          <div className="flex items-start justify-between border-b-2 border-primary pb-6">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Quotation</div>
              <div className="text-3xl font-black tracking-tight mt-1 font-mono">{q.quote_number}</div>
              <div className="text-sm text-muted-foreground mt-1">
                Issued {fmtDate(q.date)}
                {q.valid_until ? ` · Valid until ${fmtDate(q.valid_until)}` : ""}
              </div>
            </div>
            <div className="text-right">
              <div className="w-12 h-12 rounded-md bg-primary text-primary-foreground font-black text-xl flex items-center justify-center ml-auto">D</div>
              <div className="mt-2 font-display font-extrabold">{settings?.company_name || "Digitalis"}</div>
              <div className="text-xs text-muted-foreground">Digitalis CRM</div>
            </div>
          </div>

          {/* Bill to */}
          <div className="mt-6 grid grid-cols-2 gap-6 text-sm">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Billed to</div>
              <div className="font-semibold text-base">{client.company_name}</div>
              {client.contact_person && <div>{client.contact_person}</div>}
              {client.address && <div className="text-xs text-muted-foreground">{client.address}</div>}
              <div className="text-xs text-muted-foreground">{[client.city, client.state, client.country].filter(Boolean).join(", ")}</div>
              {client.gst && <div className="text-xs mt-1 font-mono">GST: {client.gst}</div>}
              {client.email && <div className="text-xs mt-1">{client.email}</div>}
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Status</div>
              <div className="inline-flex items-center h-6 px-2 rounded font-mono text-[10px] font-semibold uppercase tracking-widest bg-primary/15 text-primary">{q.status}</div>
              <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Total value</div>
              <div className="text-2xl font-black text-primary">{INR(q.final_amount)}</div>
            </div>
          </div>

          {/* Items */}
          <div className="mt-8 border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/70">
                <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="px-3 py-2">#</th>
                  <th className="px-3 py-2">Service</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Unit price</th>
                  <th className="px-3 py-2 text-right">GST</th>
                  <th className="px-3 py-2 text-right">Line total</th>
                </tr>
              </thead>
              <tbody>
                {(q.items || []).map((it, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-3 py-2 font-mono text-muted-foreground">{i + 1}</td>
                    <td className="px-3 py-2">
                      <div className="font-semibold">{it.service_name}</div>
                      {it.description && <div className="text-xs text-muted-foreground">{it.description}</div>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{it.quantity}</td>
                    <td className="px-3 py-2 text-right font-mono">{INR(it.negotiated_price || it.original_price)}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{it.tax_percent}%</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{INR(lineTotal(it))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-border font-mono">
                  <td colSpan={5} className="px-3 py-2 text-right text-muted-foreground">Subtotal</td>
                  <td className="px-3 py-2 text-right">{INR(q.subtotal)}</td>
                </tr>
                <tr className="font-mono">
                  <td colSpan={5} className="px-3 py-2 text-right text-muted-foreground">Tax</td>
                  <td className="px-3 py-2 text-right">{INR(q.tax_total)}</td>
                </tr>
                <tr className="font-mono text-base font-bold border-t-2 border-primary">
                  <td colSpan={5} className="px-3 py-2 text-right">Total payable</td>
                  <td className="px-3 py-2 text-right text-primary">{INR(q.final_amount)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {q.terms && (
            <div className="mt-6 text-sm">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Terms and conditions</div>
              <div className="whitespace-pre-wrap">{q.terms}</div>
            </div>
          )}
          {q.notes && (
            <div className="mt-4 text-sm">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Notes</div>
              <div className="whitespace-pre-wrap">{q.notes}</div>
            </div>
          )}

          <div className="mt-10 text-center text-xs text-muted-foreground border-t border-border pt-4">
            Thank you for the opportunity to work with you.
          </div>
        </div>
      </div>
    </>
  );
}
